import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel, faPaperPlane, faPrint, faXmark } from '@fortawesome/pro-light-svg-icons';
import { Money } from '../../components/Money';
import type { PayrollReportRow } from '@salaries/shared';

// Floating bulk-action bar — same pattern as the deposits SelectionBar / Arbox
// Expenses BulkActions: portaled to body (iOS Safari "fixed inside overflow"
// bug), rounded pill bottom-center on desktop, full-width on mobile, blue
// action buttons separated by dividers.

interface Props {
  selected: PayrollReportRow[];
  onSend?: () => void;
  onExcel: () => void;
  onPrint: () => void;
  onClear: () => void;
}

export function PayrollBulkBar({ selected, onSend, onExcel, onPrint, onClear }: Props) {
  if (selected.length === 0) return null;
  const total = selected.reduce((s, r) => s + r.total, 0);

  const divider = <div className="w-px bg-main-text shrink-0 hidden sm:block" style={{ height: 25 }} />;

  // Blue action button (כפתור פעולות כחול) — brand blue, Ploni 600, icon + label.
  const linkBtn = (icon: typeof faPrint, label: string, onClick: () => void) => (
    <button
      onClick={onClick}
      className="flex items-center gap-2 hover:opacity-70 transition-opacity whitespace-nowrap py-1"
      style={{ color: '#1936f7', fontFamily: 'Ploni, sans-serif', fontWeight: 600 }}
    >
      <FontAwesomeIcon icon={icon} style={{ fontSize: 14 }} />
      <span>{label}</span>
    </button>
  );

  return createPortal(
    <div
      dir="rtl"
      className="fixed bottom-0 left-0 right-0 sm:bottom-6 sm:right-auto sm:left-1/2 sm:-translate-x-1/2 z-50 sm:!w-[60%] sm:min-w-[575px]"
    >
      <div
        className="bg-white sm:rounded shadow-popup flex items-center sm:justify-between px-2 sm:px-4 py-2 sm:py-0 sm:h-[46px] gap-x-3 gap-y-2 flex-wrap sm:flex-nowrap sm:overflow-x-auto"
        style={{ boxShadow: '0 8px 24px rgba(0,0,0,0.16)' }}
      >
        <span
          className="hidden sm:inline text-main-text whitespace-nowrap"
          style={{ fontFamily: 'Ploni, sans-serif', fontWeight: 600 }}
        >
          {selected.length} נבחרו
        </span>
        {divider}
        <div className="flex items-center gap-2 whitespace-nowrap" style={{ fontFamily: 'Ploni, sans-serif' }}>
          <span className="text-secondary-text">סה"כ</span>
          <span className="text-main-text font-semibold">
            <Money value={total} />
          </span>
        </div>
        {divider}
        {onSend && linkBtn(faPaperPlane, 'שלח שכר לאישור', onSend)}
        {onSend && divider}
        {linkBtn(faFileExcel, 'ייצוא לאקסל', onExcel)}
        {divider}
        {linkBtn(faPrint, 'הדפסה', onPrint)}
        <button
          onClick={onClear}
          className="text-secondary-text hover:text-main-text transition-colors shrink-0 ms-auto sm:ms-0"
          aria-label="סגור"
        >
          <FontAwesomeIcon icon={faXmark} style={{ fontSize: 14 }} />
        </button>
      </div>
    </div>,
    document.body,
  );
}
